import { sortBy } from 'lodash'
import { z } from 'zod'

import { zodDate } from '../utilities/zodDate'
import { attendeeProperties } from './Attendee'

/**********************************************************************
 * UserProperties
 **********************************************************************/
export const userProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),
  uuid: z.string(),

  isAnonymous: z.boolean().nullish(),

  clientUuid: z.string().nullish(),

  role: z.string(),
  status: z.string(),
  createdAt: zodDate(),
  deletedAt: zodDate().nullish(),

  firstname: z.string(),
  lastname: z.string(),
  position: z.string().nullish(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  avatarUrl: z.string().nullish(),

  groups: z
    .array(
      z.object({
        uuid: z.string(),
      })
    )
    .nullish(),

  passwordResetRequired: z.boolean().nullish(),
}

const userPropertiesParser = z.object(userProperties)

export type UserProperties = z.infer<typeof userPropertiesParser>

/**********************************************************************
 * Asset
 **********************************************************************/
export const assetProperties = {
  uuid: z.string(),
  learningItemUuid: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  byteSize: z.number().nullish(),
  description: z.string().nullish(),
  visibleFrom: zodDate().nullish(),
  visibleUntil: zodDate().nullish(),
  downloadable: z.boolean(),
  updatedAt: zodDate(),
  createdAt: zodDate(),
  deletedAt: zodDate().nullish(),
}

/**********************************************************************
 * BookingProperties
 **********************************************************************/
export const bookingProperties = {
  uuid: z.string(),
  clientUuid: z.string(),
  userUuid: z.string(),
  onDemandCourseUuid: z.string().nullish(),
  scheduledCourseEventUuid: z.string().nullish(),
  type: z.string(),
  updatedAt: zodDate(),
  createdAt: zodDate(),
  deletedAt: zodDate().nullish(),
}

/**********************************************************************
 * CategoryProperties
 **********************************************************************/
export const categoryProperties = {
  id: z.number().optional(),
  uuid: z.string(),
  name: z.string(),
  slug: z.string().nullish(),
  short_description: z.string().nullish(),
  long_description: z.string().nullish(),
  deleted_at: zodDate().nullish(),
  updated_at: zodDate().nullish(),
  createdAt: zodDate().nullish(),
}

/**********************************************************************
 * CommentProperties
 **********************************************************************/
export const commentProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),
  uuid: z.string(),

  learningItemUuid: z.string().nullish(),
  groupUuid: z.string().nullish(),

  userUuid: z.string().nullish(),
  user: z.preprocess(
    (v) => v || undefined,
    z.object(userProperties).default(() => ({
      isAnonymous: true,
      uuid: '',
      status: '',
      role: '',
      createdAt: '1970-01-01T00:00:00Z',
      firstname: '',
      lastname: '',
    }))
  ),

  parentUuid: z.string().nullish(),
  level: z.number(),
  title: z.string().nullish(),
  text: z.string().nullish(),
  deletedAt: zodDate().nullish(),
  updatedAt: zodDate().nullish(),
  createdAt: zodDate(),
}

/**********************************************************************
 * CertificateProperties
 **********************************************************************/
export const certificateProperties = {
  uuid: z.string(),
  name: z.string(),
  title: z.string(),
  completedAt: zodDate().nullish(),
  validUntil: zodDate().nullish(),
  certificateId: z.string().nullish(),
  certificateUrl: z.string().nullish(),
}

/**********************************************************************
 * GroupProperties
 **********************************************************************/
export const groupProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),
  uuid: z.string(),

  clientUuid: z.string(),

  name: z.string(),

  createdAt: zodDate(),
  updatedAt: zodDate().nullish(),
}

const groupPropertiesParser = z.object(groupProperties)

export type GroupProperties = z.infer<typeof groupPropertiesParser>

/**********************************************************************
 * GroupLearningAssignmentProperties
 **********************************************************************/
export const groupLearningAssignmentProperties = {
  isDummy: z.boolean().optional(),

  uuid: z.string(),
  clientUuid: z.string(),

  learningItemUuid: z.string(),
  groupUuid: z.string(),

  dueDate: zodDate().nullish(),

  createdAt: zodDate().nullish(),
  updatedAt: zodDate().nullish(),

  group: z.object(groupProperties).nullish(),
}

/**********************************************************************
 * ProviderProperties
 **********************************************************************/
export const providerProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),

  uuid: z.string(),

  clientUuid: z.string().nullish(),

  name: z.string(),

  avatar: z.string().nullish(),
  address: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  contactPerson: z.string().nullish(),

  deletedAt: zodDate().nullish(),
  createdAt: zodDate(),
  updatedAt: zodDate(),
}

/**********************************************************************
 * LearningItemProperies
 **********************************************************************/
export enum LearningItemStatus {
  Draft = 'draft',
  Published = 'published',
  Deprectated = 'deprecated',
}

/**********************************************************************
 * UserLearningAssignmentProperties
 **********************************************************************/
export const userLearningAssignmentProperties = {
  isDummy: z.boolean().optional(),
  uuid: z.string(),
  clientUuid: z.string(),
  learningItemUuid: z.string(),
  userUuid: z.string(),
  assignedByUserUuid: z.string().nullish(),
  assignedByGroupUuid: z.string().nullish(),
  dueDate: zodDate().nullish(),
  startedAt: zodDate().nullish(),
  lastActionAt: zodDate().nullish(),
  finishedAt: zodDate().nullish(),
  createdAt: zodDate().nullish(),
  updatedAt: zodDate().nullish(),

  user: z.object(userProperties).nullish(),
}

export const learningItemProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),

  uuid: z.string(),
  clientUuid: z.string().nullish(),

  // learningPathUuid: z.string().nullish(), // unnecessary, the learning path is included
  // courseUuid: z.string().nullish(), // unnecessary, the course is included
  // scheduledCourseUuid: z.string().nullish(), // unnecessary, the scheduled course is included

  categories: z
    .array(z.object(categoryProperties))
    .nullish()
    .transform((v) => v || []), // FIXME

  providerUuid: z.string().nullish(),

  status: z.nativeEnum(LearningItemStatus),

  type: z.string(),
  title: z.string(),
  // description: z.string().nullish(),
  description: z.string(),
  imageFileName: z.string().nullish(),
  thumbnailFileName: z.string().nullish(),

  isFavorite: z.boolean(),

  provider: z
    .object({
      ...providerProperties,
    })
    .nullish(),

  createCertificate: z.boolean(),
  certificateValidUntil: zodDate().nullish(),

  groupLearningAssignments: z.array(z.object(groupLearningAssignmentProperties)).nullish(),
  userLearningAssignments: z.array(z.object(userLearningAssignmentProperties)).nullish(),
}

/**********************************************************************
 * NotificationProperties
 **********************************************************************/
export const notificationProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),

  uuid: z.string(),
  type: z.string(),
  notifiableType: z.string(),
  notifiableId: z.string(),
  data: z.object({}).passthrough(),
  readAt: zodDate().nullish(),
  createdAt: zodDate(),
  updatedAt: zodDate(),
  deletedAt: zodDate().nullish(),
}

/**********************************************************************
 * OnDemandCourseProperties
 **********************************************************************/
export const onDemandCourseProperties = {
  uuid: z.string(),
  durationInMin: z.number(),
  contentLocales: z.array(z.string()).nullish(),
  audioLocales: z.array(z.string()).nullish(),
  subtitleLocales: z.array(z.string()).nullish(),
  externalId: z.string(),
  bookings: z.array(z.object(bookingProperties)).nullish(),
  hasBookings: z.boolean(),
}

/**********************************************************************
 * ScheduledCourseProperties
 **********************************************************************/
export const scheduledCourseProperties = {
  uuid: z.string(),
  clientUuid: z.string().nullish(),
  hasWaitingList: z.boolean(),
  hasBookings: z.boolean(),
}

/**********************************************************************
 * ScheduledCourseEventProperties
 **********************************************************************/
export enum ScheduledCourseEventStatus {
  Bookable = 'bookable',
  FullyBooked = 'fully_booked',
}

export const scheduledCourseEventProperties = {
  uuid: z.string(),

  maxAttendees: z.number().nullish(),

  startDate: zodDate(),
  endDate: zodDate(),

  dates: z
    .array(
      z.object({
        uuid: z.string(),

        startDate: zodDate(),
        endDate: zodDate(),

        meetingLink: z.string().nullish(),
        meetingRoom: z.string().nullish(),
        meetingAddress: z.string().nullish(),
        maxAttendeesVirtual: z.number().nullish(),
        maxAttendeesOffice: z.number().nullish(),
        maxAttendeesExternal: z.number().nullish(),

        trainers: z.array(z.object(userProperties)).nullish(),

        attendees: z.array(z.object(attendeeProperties)).nullish(),

        title: z.string().nullish(),
      })
    )
    .transform((d) => sortBy(d, 'startDate')),

  status: z.nativeEnum(ScheduledCourseEventStatus),
  seatsAvailable: z.number(),

  participants: z.array(z.object(userProperties)),

  // A booking of a currently logged-in user
  booking: z.object(bookingProperties).nullish(),
}

/**********************************************************************
 * LearningPathProperties
 **********************************************************************/
export const learningPathProperties = {
  uuid: z.string(),

  clientUuid: z.string().nullish(),

  courses: z
    .array(
      z.object({
        uuid: z.string().uuid(),
      })
    )
    .nullish(),
}

/**********************************************************************
 * TrainerProperties
 **********************************************************************/
export const trainerProperties = {
  isDummy: z.boolean().optional(),
  error: z.any().optional(),
  uuid: z.string(),

  clientUuid: z.string().nullish(),

  createdAt: zodDate(),

  firstname: z.string(),
  lastname: z.string(),
}
